import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Plus, RefreshCw, Mail, Loader2, Search, MailWarning, Link2, ExternalLink, AlertTriangle, ArchiveRestore, ShieldAlert, Archive, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import {
  PageHeader, PANEL_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS,
} from "@/components/premium";
import { InviteBusinessDialog } from "@/components/InviteBusinessDialog";

/**
 * Build the public booking URL for a business slug. Uses
 * REACT_APP_BACKEND_URL as the base (mirrors what Settings.jsx does for
 * the tenant's own booking link).
 */
function bookingUrlFor(slug) {
  if (!slug) return "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  // The frontend and backend share the same origin behind the ingress —
  // use the location origin as a runtime fallback to keep the link in sync
  // with whichever host the operator is currently logged in on.
  const origin = base || (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin}/book/${slug}`;
}

/**
 * Copy text to clipboard with a graceful fallback for browsers/contexts
 * where navigator.clipboard rejects (NotAllowedError, non-secure, etc.).
 */
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (_) {
      return false;
    }
  }
}

async function copyBookingLink(slug) {
  const ok = await copyToClipboard(bookingUrlFor(slug));
  if (ok) toast.success("Bookinglenke kopiert");
  else toast.error("Kunne ikke kopiere bookinglenke");
}

// ----------------------------------------------------------------------
// Label maps (Norwegian Bokmål) for the table + edit form.
// ----------------------------------------------------------------------

const PLAN_LABEL = { demo: "Demo", basic: "Basic", pro: "Pro", premium: "Premium" };
const PLAN_TONE = {
  demo: "bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30",
  basic: "bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/30",
  pro: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30",
  premium: "bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/30",
};

const INVITE_LABEL = {
  pending: "Venter aktivering",
  activated: "Aktivert",
  expired: "Utløpt",
  missing: "Mangler",
};
const INVITE_TONE = {
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30",
  activated: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30",
  expired: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30",
  missing: "bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30",
};

const ACCESS_LABEL = { active: "Aktiv", disabled: "Deaktivert" };
const ACCESS_TONE = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30",
  disabled: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30",
};

const BILLING_LABEL = {
  not_decided: "Ikke avgjort",
  free_pilot: "Gratis pilot",
  manual: "Manuell faktura",
};

// ----------------------------------------------------------------------

function StatusPill({ tone, label, testid }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold text-[10px] uppercase tracking-wider ring-1 ring-inset ${tone}`}
      data-testid={testid}
    >
      {label}
    </span>
  );
}

// ---------- Plan filter pill card ----------

const PLAN_TONE_FILTER = {
  emerald: {
    active: "border-emerald-500/40 bg-emerald-500/[0.08] shadow-[0_8px_30px_-10px_rgba(16,185,129,0.5)]",
    inactive: "border-emerald-500/10 hover:border-emerald-500/30",
    accent: "text-emerald-600 dark:text-emerald-300",
  },
  sky: {
    active: "border-sky-500/40 bg-sky-500/[0.08] shadow-[0_8px_30px_-10px_rgba(14,165,233,0.45)]",
    inactive: "border-sky-500/10 hover:border-sky-500/30",
    accent: "text-sky-600 dark:text-sky-300",
  },
  violet: {
    active: "border-violet-500/40 bg-violet-500/[0.08] shadow-[0_8px_30px_-10px_rgba(139,92,246,0.45)]",
    inactive: "border-violet-500/10 hover:border-violet-500/30",
    accent: "text-violet-600 dark:text-violet-300",
  },
};

function PlanFilterCard({ label, count, active, onClick, testid, tone = "emerald" }) {
  const t = PLAN_TONE_FILTER[tone] || PLAN_TONE_FILTER.emerald;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active}
      aria-pressed={active}
      className={`text-left rounded-2xl border bg-card p-4 sm:p-5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${
        active ? t.active : `${t.inactive} hover:bg-emerald-500/[0.03]`
      }`}
    >
      <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${active ? t.accent : "text-muted-foreground"}`}>
        {label}
      </div>
      <div className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight">
        {count}
      </div>
    </button>
  );
}

export default function AdminBusinesses() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all"); // all | basic | pro | premium
  const [showArchived, setShowArchived] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // Gate access: only Super Admin (home business `is_admin: true`).
  useEffect(() => {
    let mounted = true;
    api.get("/business")
      .then((r) => {
        if (!mounted) return;
        if (r.data?.is_admin) setAuthorized(true);
        else {
          setAuthorized(false);
          toast.error("Du har ikke tilgang til Bedrifter.");
          navigate("/dashboard", { replace: true });
        }
      })
      .catch(() => {
        if (!mounted) return;
        setAuthorized(false);
        navigate("/dashboard", { replace: true });
      });
    return () => { mounted = false; };
  }, [navigate]);

  const load = async ({ withToast = false } = {}) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/businesses", {
        params: showArchived ? { include_archived: "true" } : undefined,
      });
      setItems(r.data.items || []);
      if (withToast) toast.success("Bedrifter oppdatert");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunne ikke oppdatere bedrifter. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authorized === true) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, showArchived]);

  // Counts per plan — used by the filter pills above the table. Demo and
  // legacy/missing plans are NOT exposed as filters (we never invite new
  // businesses on Demo), but they still appear in the "Alle" bucket.
  const planCounts = useMemo(() => {
    const c = { all: 0, basic: 0, pro: 0, premium: 0 };
    (items || []).forEach((b) => {
      c.all += 1;
      if (b.plan === "basic" || b.plan === "pro" || b.plan === "premium") {
        c[b.plan] += 1;
      }
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    return items.filter((b) => {
      if (planFilter !== "all" && b.plan !== planFilter) return false;
      if (!q) return true;
      const hay = [b.name, b.slug, b.owner_email, b.internal_note]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, planFilter]);

  return (
    <div className="space-y-6" data-testid="admin-businesses-page">
      {authorized !== true ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
          Sjekker tilgang...
        </div>
      ) : (
        <>
          <PageHeader
            eyebrow="Plattform-admin"
            title="Bedrifter"
            subtitle="Inviter, administrer og styr tilgang til ClientFlow-kontoer. Bruk-inntekt og fakturering håndteres separat — invitasjon her gir kun tilgang."
            right={
              <>
                <Button
                  variant="outline"
                  className={SECONDARY_BTN_CLS}
                  onClick={() => load({ withToast: true })}
                  disabled={loading}
                  data-testid="admin-bedrifter-refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
                  Oppdater
                </Button>
                <Button
                  className={PRIMARY_BTN_CLS}
                  onClick={() => setInviteOpen(true)}
                  data-testid="admin-bedrifter-invite-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                  Inviter bedrift
                </Button>
              </>
            }
          />

          {/* Plan summary + filter pills */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-bedrifter-plan-filters">
            <PlanFilterCard
              testid="plan-filter-all" label="Alle" count={planCounts.all}
              active={planFilter === "all"} onClick={() => setPlanFilter("all")}
              tone="emerald"
            />
            <PlanFilterCard
              testid="plan-filter-basic" label="Basic" count={planCounts.basic}
              active={planFilter === "basic"} onClick={() => setPlanFilter("basic")}
              tone="sky"
            />
            <PlanFilterCard
              testid="plan-filter-pro" label="Pro" count={planCounts.pro}
              active={planFilter === "pro"} onClick={() => setPlanFilter("pro")}
              tone="emerald"
            />
            <PlanFilterCard
              testid="plan-filter-premium" label="Premium" count={planCounts.premium}
              active={planFilter === "premium"} onClick={() => setPlanFilter("premium")}
              tone="violet"
            />
          </div>

          <div className={`${PANEL_CLS} p-4 sm:p-5`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 sm:max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Søk på navn, slug, e-post eller notat…"
                  className="pl-9 rounded-xl"
                  data-testid="admin-bedrifter-search"
                />
              </div>
              <label
                className="inline-flex items-center gap-2 text-sm cursor-pointer select-none"
                data-testid="admin-bedrifter-show-archived-toggle"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border accent-emerald-500"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <span className="text-muted-foreground">Vis arkiverte</span>
              </label>
            </div>
          </div>

          <div className={`${PANEL_CLS} overflow-hidden`}>
            {loading && !items ? (
              <div className="p-10 text-center text-muted-foreground">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
                Laster bedrifter…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center" data-testid="admin-bedrifter-empty">
                <Building2 className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" strokeWidth={1.5} />
                <p className="font-medium">Ingen bedrifter matcher søket.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-500/[0.04] text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Bedrift</th>
                      <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Eier</th>
                      <th className="text-left font-semibold px-4 py-3">Pakke</th>
                      <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Fakturering</th>
                      <th className="text-left font-semibold px-4 py-3">Tilgang</th>
                      <th className="text-left font-semibold px-4 py-3">Invitasjon</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {filtered.map((b) => (
                      <tr
                        key={b.id}
                        className="hover:bg-emerald-500/[0.03] transition-colors"
                        data-testid={`admin-biz-row-${b.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium flex items-center gap-2">
                            {b.name}
                            {b.is_admin && (
                              <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                                ClientFlow HQ
                              </Badge>
                            )}
                            {b.archived_at && (
                              <Badge
                                variant="outline"
                                className="rounded-full text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
                                data-testid={`admin-biz-archived-pill-${b.id}`}
                              >
                                Arkivert
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">/{b.slug}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="text-sm truncate max-w-[220px]">{b.owner_email || "—"}</div>
                          {b.internal_note && (
                            <div className="text-xs text-muted-foreground truncate max-w-[220px]" title={b.internal_note}>
                              {b.internal_note}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill
                            tone={PLAN_TONE[b.plan] || PLAN_TONE.demo}
                            label={PLAN_LABEL[b.plan] || b.plan}
                            testid={`admin-biz-plan-${b.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                          {BILLING_LABEL[b.billing_status] || "—"}
                          {b.pilot_end_date && (
                            <div>Pilot t.o.m. {b.pilot_end_date}</div>
                          )}
                          {typeof b.agreed_monthly_price_nok === "number" && (
                            <div>{b.agreed_monthly_price_nok} kr/mnd</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill
                            tone={ACCESS_TONE[b.access_status] || ACCESS_TONE.active}
                            label={ACCESS_LABEL[b.access_status] || b.access_status}
                            testid={`admin-biz-access-${b.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill
                            tone={INVITE_TONE[b.invite_status] || INVITE_TONE.missing}
                            label={INVITE_LABEL[b.invite_status] || b.invite_status}
                            testid={`admin-biz-invite-${b.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg gap-1.5"
                              onClick={(e) => { e.stopPropagation(); copyBookingLink(b.slug); }}
                              disabled={b.is_admin}
                              title={b.is_admin ? "ClientFlow HQ er ikke offentlig" : "Kopier bookinglenke"}
                              data-testid={`admin-biz-copy-link-${b.id}`}
                            >
                              <Link2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                              Kopier lenke
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg"
                              onClick={() => setEditTarget(b)}
                              data-testid={`admin-biz-edit-${b.id}`}
                            >
                              Administrer
                            </Button>
                            {b.archived_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 rounded-lg text-emerald-600 hover:text-emerald-700"
                                onClick={async () => {
                                  if (!window.confirm(`Gjenopprette «${b.name}»?`)) return;
                                  try {
                                    await api.post(`/admin/businesses/${b.id}/restore`);
                                    toast.success("Bedriften er gjenopprettet");
                                    load();
                                  } catch (err) {
                                    const d = err?.response?.data?.detail;
                                    toast.error(typeof d === "string" ? d : "Kunne ikke gjenopprette");
                                  }
                                }}
                                data-testid={`admin-biz-restore-${b.id}`}
                              >
                                <ArchiveRestore className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Gjenopprett
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <InviteBusinessDialog
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            onCreated={() => { setInviteOpen(false); load(); }}
          />
          <BusinessEditDialog
            target={editTarget}
            onClose={() => setEditTarget(null)}
            onSaved={() => { setEditTarget(null); load(); }}
          />
        </>
      )}
    </div>
  );
}

// ====================================================================
// (InviteBusinessDialog moved to /components/InviteBusinessDialog.jsx
//  so the Forespørsler page can reuse it for the one-click "Konverter
//  forespørsel" flow.)
// ====================================================================

// ====================================================================
// Edit dialog — change plan / access / billing / pilot date / note +
// resend invitation if still pending.
// ====================================================================

function BusinessEditDialog({ target, onClose, onSaved }) {
  const open = !!target;
  const [form, setForm] = useState({});
  const initialRef = React.useRef({});
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [sendingResetLink, setSendingResetLink] = useState(false);

  useEffect(() => {
    if (!target) return;
    const initial = {
      plan: target.plan || "basic",
      access_status: target.access_status || "active",
      billing_status: target.billing_status || "not_decided",
      pilot_end_date: target.pilot_end_date || "",
      agreed_monthly_price_nok:
        typeof target.agreed_monthly_price_nok === "number"
          ? String(target.agreed_monthly_price_nok) : "",
      internal_note: target.internal_note || "",
    };
    initialRef.current = initial;
    setForm(initial);
  }, [target]);

  if (!target) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Diff form against the initial snapshot. We only send fields the
  // operator actually touched — this also lets us detect a "no-op save"
  // and surface clear feedback instead of pretending we updated nothing.
  const buildPatch = () => {
    const diff = {};
    const init = initialRef.current;
    Object.keys(form).forEach((k) => {
      if (form[k] !== init[k]) diff[k] = form[k];
    });
    return diff;
  };
  const hasChanges = Object.keys(buildPatch()).length > 0;

  const save = async () => {
    const diff = buildPatch();
    if (Object.keys(diff).length === 0) {
      toast.info("Ingen endringer å lagre.");
      return;
    }
    setSaving(true);
    try {
      // Coerce the few fields that need type-massaging on the wire.
      const payload = { ...diff };
      if ("pilot_end_date" in payload) {
        payload.pilot_end_date = payload.pilot_end_date || null;
      }
      if ("agreed_monthly_price_nok" in payload) {
        payload.agreed_monthly_price_nok =
          payload.agreed_monthly_price_nok === "" ? null : Number(payload.agreed_monthly_price_nok);
      }
      await api.patch(`/admin/businesses/${target.id}`, payload);
      // Close the dialog FIRST, then fire the toast on the next tick.
      // This matches the "Forespørsler oppdatert" flow exactly — the toast
      // renders against a clean background after the modal teardown,
      // making it unmistakably visible in the top-right corner.
      onSaved();
      setTimeout(() => toast.success("Endringer lagret"), 60);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Kunne ikke lagre endringene. Prøv igjen.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post(`/admin/invitations/${target.id}/resend`);
      onSaved();
      if (r.data?.email_sent === false) {
        setTimeout(() => toast.warning("Invitasjonen ble opprettet, men e-posten kunne ikke sendes.", {
          description: "Sjekk e-postoppsettet (Resend-API-nøkkel og avsenderdomene) og prøv på nytt.",
          duration: 10000,
        }), 60);
      } else {
        setTimeout(() => toast.success("Ny invitasjon sendt på e-post."), 60);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunne ikke sende ny invitasjon");
    } finally {
      setResending(false);
    }
  };

  const canResend = target.invite_status !== "activated";

  const sendPasswordResetLink = async () => {
    setSendingResetLink(true);
    try {
      await api.post(`/admin/businesses/${target.id}/send-password-reset`);
      toast.success("Passordlenke sendt");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // For 4xx with a known reason (no owner / no email / archived), show
      // the server's exact Norwegian copy. For everything else (network,
      // 5xx, email-provider failure) fall back to the generic copy the
      // user spec asked for.
      const status = err?.response?.status;
      const msg = (typeof detail === "string" && status >= 400 && status < 500)
        ? detail
        : "Kunne ikke sende passordlenke. Prøv igjen.";
      toast.error(msg);
    } finally {
      setSendingResetLink(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-bedrift-dialog">
        <DialogHeader>
          <DialogTitle>{target.name}</DialogTitle>
          <DialogDescription>
            <span className="text-xs text-muted-foreground">/{target.slug}</span>
            {target.owner_email && <> · {target.owner_email}</>}
          </DialogDescription>
        </DialogHeader>

        {target.is_admin && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-3 text-xs" data-testid="edit-hq-note">
            <strong>ClientFlow HQ</strong> er plattformens administratorkonto og kan ikke deaktiveres her. Super Admin-rettigheter styres separat fra produktpakken — du kan trygt oppgradere internkontoen til Premium for interntest uten å miste plattform-admin-tilgang.
          </div>
        )}

        {!target.is_admin && (
          <div
            className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] p-3"
            data-testid="edit-booking-link-box"
          >
            <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
              Bookinglenke
            </Label>
            <div className="mt-1.5 rounded-lg border border-border bg-background/60 px-3 py-2 overflow-hidden">
              <div
                className="text-xs font-mono break-all"
                data-testid="edit-booking-url"
              >
                {bookingUrlFor(target.slug)}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 justify-end">
              <a
                href={bookingUrlFor(target.slug)} target="_blank" rel="noreferrer"
                data-testid="edit-open-link"
              >
                <Button type="button" variant="outline" size="sm" className="h-8 rounded-lg gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Åpne
                </Button>
              </a>
              <Button
                type="button" variant="outline" size="sm" className="h-8 rounded-lg gap-1.5"
                onClick={() => copyBookingLink(target.slug)}
                data-testid="edit-copy-link"
              >
                <Link2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                Kopier lenke
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pakke</Label>
              <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
                <SelectTrigger data-testid="edit-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tilgang</Label>
              <Select
                value={form.access_status}
                onValueChange={(v) => set("access_status", v)}
                disabled={target.is_admin}
              >
                <SelectTrigger data-testid="edit-access"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  {!target.is_admin && (
                    <SelectItem value="disabled">Deaktivert</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {target.is_admin && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="edit-hq-access-lock">
                  Kan ikke deaktiveres.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fakturering</Label>
              <Select value={form.billing_status} onValueChange={(v) => set("billing_status", v)}>
                <SelectTrigger data-testid="edit-billing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_pilot">Gratis pilot</SelectItem>
                  <SelectItem value="manual">Manuell faktura</SelectItem>
                  <SelectItem value="not_decided">Ikke avgjort</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-pilot-end">Pilot t.o.m.</Label>
              <Input
                id="edit-pilot-end" type="date" value={form.pilot_end_date}
                onChange={(e) => set("pilot_end_date", e.target.value)}
                data-testid="edit-pilot-end"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-price">Avtalt pris (kr/mnd)</Label>
            <Input
              id="edit-price" type="number" min={0} value={form.agreed_monthly_price_nok}
              onChange={(e) => set("agreed_monthly_price_nok", e.target.value)}
              data-testid="edit-price"
            />
          </div>

          <div>
            <Label htmlFor="edit-note">Internt notat</Label>
            <Textarea
              id="edit-note" rows={3} maxLength={2000}
              value={form.internal_note}
              onChange={(e) => set("internal_note", e.target.value)}
              data-testid="edit-note"
            />
          </div>

          {canResend && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-3 text-sm">
              <MailWarning className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-300 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">
                  {target.invite_status === "pending" && "Invitasjon ikke aktivert ennå."}
                  {target.invite_status === "expired" && "Invitasjonen er utløpt."}
                  {target.invite_status === "missing" && "Ingen invitasjon registrert."}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Send en ny aktiveringslenke til {target.owner_email}.
                </div>
              </div>
              <Button
                variant="outline" className={SECONDARY_BTN_CLS}
                onClick={resend} disabled={resending}
                data-testid="edit-resend-invite"
              >
                {resending && <Loader2 className="w-4 h-4 animate-spin" />}
                Send ny invitasjon
              </Button>
            </div>
          )}

          {/* Kontosupport — Super Admin-only password-reset helper.
              Shown only for non-HQ, non-archived rows whose owner has
              ALREADY activated their invitation. For pending/expired
              invites the resend-invite block above is the right CTA. */}
          {!target.is_admin && !target.archived_at && !canResend && target.owner_email && (
            <div
              className="flex items-start gap-3 rounded-xl border border-sky-500/30 bg-sky-500/[0.05] p-3 text-sm"
              data-testid="account-support-panel"
            >
              <KeyRound className="w-4 h-4 mt-0.5 text-sky-600 dark:text-sky-300 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Kontosupport</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Send en sikker passordlenke til {target.owner_email}. Lenken er
                  gyldig i 45 minutter og kan kun brukes én gang. Du får ikke se
                  passordet eller lenken.
                </div>
              </div>
              <Button
                variant="outline" className={SECONDARY_BTN_CLS}
                onClick={sendPasswordResetLink} disabled={sendingResetLink}
                data-testid="edit-send-password-reset"
              >
                {sendingResetLink && <Loader2 className="w-4 h-4 animate-spin" />}
                Send passordlenke
              </Button>
            </div>
          )}

          {/* Faresone — archive (only for unprotected, non-archived rows) */}
          {!target.is_admin && target.slug !== "nordlys-frisor" && !target.archived_at && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-3" data-testid="faresone-panel">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-300 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-rose-700 dark:text-rose-300">Faresone</div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Arkiver bedriften for å skjule den fra aktive bedrifter og stenge tilgang for eier/ansatte. Data beholdes for revisjon — kan gjenopprettes senere.
                  </p>
                </div>
                <Button
                  variant="outline" size="sm"
                  className="h-8 rounded-lg border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10"
                  onClick={() => setArchiveOpen(true)}
                  data-testid="faresone-archive-btn"
                >
                  Arkiver bedrift
                </Button>
              </div>
            </div>
          )}
          {target.archived_at && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-3 text-xs" data-testid="archived-info">
              <div className="font-semibold text-rose-700 dark:text-rose-300">Arkivert</div>
              <div className="text-muted-foreground mt-1">
                Arkivert {new Date(target.archived_at).toLocaleString("nb-NO")}
                {target.archived_by_email && <> av {target.archived_by_email}</>}
                {target.archive_reason && <> · «{target.archive_reason}»</>}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline" className={SECONDARY_BTN_CLS}
            onClick={onClose} disabled={saving}
          >
            Lukk
          </Button>
          <Button
            className={PRIMARY_BTN_CLS} onClick={save} disabled={saving || !hasChanges}
            data-testid="edit-save"
            title={hasChanges ? undefined : "Ingen endringer å lagre."}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Lagre endringer
          </Button>
        </DialogFooter>
      </DialogContent>

      <ArchiveConfirmDialog
        open={archiveOpen}
        target={target}
        onOpenChange={setArchiveOpen}
        onArchived={() => {
          setArchiveOpen(false);
          // Close the edit dialog AND refresh the list. Schedule the
          // success toast after teardown so it renders against a clean
          // background — matches the pattern used by `save()` above.
          onSaved();
          setTimeout(() => toast.success("Bedriften er arkivert"), 60);
        }}
      />
    </Dialog>
  );
}

// ====================================================================
// Archive confirmation dialog — destructive, requires fresh password
// re-auth + exact business name match. Optional reason for audit log.
// ====================================================================

function ArchiveConfirmDialog({ open, target, onOpenChange, onArchived }) {
  const [password, setPassword] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever the dialog (re)opens or target changes — never
  // carry credentials between sessions of this modal.
  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirmName("");
      setReason("");
      setSubmitting(false);
    }
  }, [open, target?.id]);

  if (!target) return null;
  const expectedName = (target.name || "").trim();
  const nameMatches = confirmName.trim() === expectedName;
  const canSubmit = !submitting && password.length > 0 && nameMatches;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/businesses/${target.id}/archive`, {
        password,
        confirm_name: confirmName,
        reason: reason.trim() || null,
      });
      onArchived();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : "Kunne ikke arkivere bedriften. Prøv igjen.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md" data-testid="archive-confirm-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-rose-600 dark:text-rose-300">
            <Archive className="w-5 h-5" strokeWidth={1.75} />
            <DialogTitle>Arkiver «{expectedName}»</DialogTitle>
          </div>
          <DialogDescription>
            Bedriften vil skjules fra aktive bedrifter og eier/ansatte mister
            tilgang umiddelbart. Data beholdes for revisjon og kan gjenopprettes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-3 text-xs text-muted-foreground flex gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-300 shrink-0 mt-0.5" />
            <span>
              Dette krever at du bekrefter med ditt eget passord og skriver
              bedriftsnavnet nøyaktig som vist over.
            </span>
          </div>

          <div>
            <Label htmlFor="archive-password">Ditt passord</Label>
            <PasswordInput
              id="archive-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="archive-password"
              toggleTestId="archive-password-toggle"
            />
          </div>

          <div>
            <Label htmlFor="archive-confirm-name">
              Skriv bedriftsnavn for å bekrefte
            </Label>
            <Input
              id="archive-confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={expectedName}
              autoComplete="off"
              required
              data-testid="archive-confirm-name"
            />
            {confirmName.length > 0 && !nameMatches && (
              <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1" data-testid="archive-name-mismatch">
                Navnet stemmer ikke med «{expectedName}».
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="archive-reason">Begrunnelse (valgfri)</Label>
            <Textarea
              id="archive-reason"
              rows={2}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="F.eks. avsluttet samarbeid, dobbeltregistrering …"
              data-testid="archive-reason"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className={SECONDARY_BTN_CLS}
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="archive-cancel"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              className="h-10 rounded-xl gap-2 bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!canSubmit}
              data-testid="archive-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              <Archive className="w-4 h-4" strokeWidth={1.75} />
              Arkiver bedrift
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
